/**
 * How the conversations menu slices the thread list: ownership, search, and
 * recency groups with the client's Updates thread first and pinned threads
 * (the case's CaseBlink Workflow thread, say) next. Pure functions so the
 * menu's rules are testable.
 */
import type { AgentThread } from "../api/agentApi";
import { recencyBucket } from "./time";

/** "Mine": the person started the thread or has posted in it. */
export const isMyThread = (
  thread: AgentThread,
  meUid: string | null
): boolean => {
  if (!meUid) return false;
  return (
    thread.created_by === meUid || (thread.participants ?? []).includes(meUid)
  );
};

/** Started by `uid`; `null` is anyone. */
export const threadStartedBy = (
  thread: AgentThread,
  uid: string | null
): boolean => !uid || thread.created_by === uid;

/** Case-insensitive match on the title and the last message. */
export const threadMatchesQuery = (
  thread: AgentThread,
  query: string
): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    thread.title.toLowerCase().includes(q) ||
    (thread.last_message_preview ?? "").toLowerCase().includes(q)
  );
};

const byUpdated = (a: AgentThread, b: AgentThread): number =>
  (b.updated_at ?? b.created_at ?? "").localeCompare(
    a.updated_at ?? a.created_at ?? ""
  );

/**
 * The conversations menu's groups: the client's Updates thread first, then
 * Pinned, Today, This Week, Earlier.
 */
export type MenuBucket = "updates" | "pinned" | "today" | "week" | "earlier";

export const MENU_BUCKET_LABEL: Record<MenuBucket, string> = {
  updates: "Updates",
  pinned: "Pinned",
  today: "Today",
  week: "This Week",
  earlier: "Earlier"
};

const MENU_ORDER: MenuBucket[] = [
  "updates",
  "pinned",
  "today",
  "week",
  "earlier"
];

/** The client's shared inbox for background runs. */
export const isUpdatesThread = (thread: AgentThread): boolean =>
  thread.kind === "updates";

export const menuBucket = (
  iso: string | null | undefined,
  now: number = Date.now()
): MenuBucket => {
  const bucket = recencyBucket(iso, now);
  if (bucket === "today") return "today";
  return bucket === "older" ? "earlier" : "week";
};

export type MenuGroup = {
  id: MenuBucket;
  label: string;
  threads: AgentThread[];
};

/** Updates, Pinned, then newest first within Today / This Week / Earlier; empty groups omitted. */
export const groupThreadsForMenu = (
  threads: AgentThread[],
  now: number = Date.now()
): MenuGroup[] => {
  const buckets = new Map<MenuBucket, AgentThread[]>();
  for (const t of [...threads].sort(byUpdated)) {
    const id = isUpdatesThread(t)
      ? "updates"
      : t.pinned && !t.archived
      ? "pinned"
      : menuBucket(t.updated_at ?? t.created_at, now);
    buckets.set(id, [...(buckets.get(id) ?? []), t]);
  }
  return MENU_ORDER.filter((id) => buckets.has(id)).map((id) => ({
    id,
    label: MENU_BUCKET_LABEL[id],
    threads: buckets.get(id) ?? []
  }));
};
